import { useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Collapse,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
} from "@mui/material";

import { useLazyGetPersonsQuery, usePatchPersonMutation } from "@services/api/persons";
import { AdditionalAccountDialogProps, OptionApplicationMenu } from "@interfaces/slices";
import { useLazyGetPersonTypesQuery } from "@services/api/catalogs";
import { PersonType } from "@interfaces/catalogs";
import { RootState, store } from "@store/store";
import { Person } from "@interfaces/persons";
import { setDialogConfig, setOptionAppSelected } from "@store/slices/componentsSlice";
import { useDispatch, useSelector } from "react-redux";
import { UUID } from "crypto";
import { usePostStatusApplicationMutation, usePutApplicationsMutation } from "@services/api/applications";
import { setApplicationStateCode } from "@store/slices/componentsSlice";
import { error } from "console";
import { filterByNestedCodes } from "@helpers/generals";

export const CuentaAdicional = (props: AdditionalAccountDialogProps) => {
    const dispatch = useDispatch();

    const hasFetched = useRef(false);

    const [error, setError] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const [persons, setPersons] = useState<Array<Person>>([]);
    const [personType, setPersonType] = useState<PersonType>();
    const [personSelected, setPersonSelected] = useState<UUID | undefined>(undefined);

    const [triggerGetPersons] = useLazyGetPersonsQuery();
    const [triggerPatchPerson] = usePatchPersonMutation();
    const [triggerGetPersonType] = useLazyGetPersonTypesQuery();
    const [triggerPutApplication] = usePutApplicationsMutation();
    const [triggerPostStatusApplication] = usePostStatusApplicationMutation();

    const userSession = useSelector((state: RootState) => state.session.user);

    const applicationSelected = (state = store.getState()) => state.components.applicationSelected;

    const handleAdditionalAccountRequested = async () => {
        try {
            setIsSubmitting(true);
            await triggerPostStatusApplication({
                applicationId: applicationSelected(store.getState()).id,
                userName:
                    userSession.name
                        ?.concat(" ", userSession.last_name ?? "", " ", userSession.last_name_2 ?? "")
                        .trim() ?? "",
                statusCode: "CSECI",
                disableSequenceCheck: true,
            }).unwrap();

            await triggerPatchPerson({
                personId: personSelected as UUID,
                body: {
                    information_person_completed: true,
                    bank_references_completed: false,
                },
            }).unwrap();

            await triggerPutApplication({
                applicationId: applicationSelected(store.getState()).id,
                body: {
                    completed: false,
                    user_id_modified: userSession.id,
                    user_name:
                        userSession.name
                            ?.concat(" ", userSession?.last_name ?? "", " ", userSession?.last_name_2 ?? "")
                            .trim() ?? "",
                },
            }).unwrap();

            dispatch(setDialogConfig());
            dispatch(setApplicationStateCode("CSECI"));
            dispatch(setOptionAppSelected(OptionApplicationMenu.EXPEDIENTE));
        } catch (error: any) {
            if (Object.keys(error).includes("status") && error.status === 400) {
                setError(error.data.errors);
                setTimeout(() => {
                    setError("");
                }, 2500);
            } else {
                console.log(error);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const loadInfo = async () => {
        try {
            const personTypes = await triggerGetPersonType().unwrap();
            setPersonType(personTypes.find((personType) => personType.code === props.personTypeCode));

            let params = new URLSearchParams([
                ["q", `loan_application_id:${applicationSelected(store.getState()).id}`],
            ]);
            const personsData = await triggerGetPersons(params.toString(), false).unwrap();

            // setPersons(personsData.filter((person) => person.person_type.code === props.personTypeCode));
            setPersons(
                filterByNestedCodes(personsData, (person) => person.person_types_detail, [props.personTypeCode]),
            );
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (hasFetched.current) return;
        loadInfo();

        hasFetched.current = true;
    }, []);

    return (
        <Box margin={4} maxHeight={"60vh"}>
            <Stack rowGap={2}>
                <Typography variant="body1" color="main3" fontWeight={600} textAlign={"center"}>
                    Solicitar cuenta adicional a {personType?.name}:
                </Typography>
                <FormControl fullWidth margin={"normal"} size="small" sx={{ maxWidth: "350px" }}>
                    <InputLabel required>{personType?.name}</InputLabel>
                    <Select
                        name="person"
                        label={personType?.name}
                        value={personSelected ?? ""}
                        onChange={(event: SelectChangeEvent<UUID>) => setPersonSelected(event.target.value)}
                    >
                        {persons.map((person) => (
                            <MenuItem key={person.id} value={person.id}>
                                {person.name} {person.last_name} {person.last_name_2}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Collapse in={error !== ""}>
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                </Collapse>
                <Stack direction={"row"} justifyContent={"space-around"} mt={2}>
                    <Button variant="blue_outlined" disabled={isSubmitting} onClick={() => dispatch(setDialogConfig())}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={personSelected === undefined}
                        loading={isSubmitting}
                        onClick={handleAdditionalAccountRequested}
                    >
                        Solicitar
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};
